import React, { Fragment, useState } from 'react';
import styled from 'styled-components';

// Capability groups for organized display
const capabilityGroups = [
  {
    title: 'Events',
    description: 'Control access to event management features',
    keys: ['events', 'scan_tickets', 'check_tickets', 'cancel_event'],
    labels: {
      events: 'View Events',
      scan_tickets: 'Scan Passes/Tickets',
      check_tickets: 'Manually Check-in',
      cancel_event: 'Cancel/Postpone Events',
    },
  },
  {
    title: 'Discounts',
    description: 'Manage promotional codes and discounts',
    keys: ['promos', 'add_promos'],
    labels: {
      promos: 'View Promo Codes',
      add_promos: 'Issue Discounts',
    },
  },
  {
    title: 'Staff',
    description: 'Manage staff member access',
    keys: ['staff', 'add_staff'],
    labels: {
      staff: 'View Staff',
      add_staff: 'Add Staff',
    },
  },
  {
    title: 'Guest Lists',
    description: 'Control guest list management',
    keys: ['guests', 'add_guests', 'check_in_guests'],
    labels: {
      guests: 'View Guest Lists',
      add_guests: 'Create Guest List',
      check_in_guests: 'Check In Guests',
    },
  },
  {
    title: 'Credits',
    description: 'Manage customer credits',
    keys: ['credits', 'issue_credits'],
    labels: {
      credits: 'View Credits',
      issue_credits: 'Issue Credits',
    },
  },
  {
    title: 'Transactions',
    description: 'View and manage customer transactions',
    keys: ['transactions', 'issue_refunds'],
    labels: {
      transactions: 'View Transactions',
      issue_refunds: 'Issue Refunds',
    },
  },
  {
    title: 'Messages',
    description: 'Access to messaging features',
    keys: ['messages', 'create_messages'],
    labels: {
      messages: 'View Messages',
      create_messages: 'Send Messages',
    },
  },
  {
    title: 'Other',
    description: 'Additional account permissions',
    keys: ['stripe', 'customers', 'edit_billing', 'pos'],
    labels: {
      stripe: 'Stripe Access',
      customers: 'View Customers',
      edit_billing: 'Edit Billing',
      pos: 'Use POS',
    },
  },
  {
    title: 'Registrations',
    description: 'Customer registration management',
    keys: ['registrations', 'edit_registrations'],
    labels: {
      registrations: 'View Registrations',
      edit_registrations: 'Edit Registrations',
    },
    hideForTicketHoss: true,
  },
  {
    title: 'Memberships',
    description: 'Membership management access',
    keys: ['memberships', 'edit_memberships'],
    labels: {
      memberships: 'View Memberships',
      edit_memberships: 'Edit Memberships',
    },
    hideForTicketHoss: true,
  },
  {
    title: 'Email Notifications',
    description: 'Control which email notifications this user receives',
    keys: [
      'registration_email',
      'annual_email',
      'membership_email',
      'refund_request_email',
    ],
    labels: {
      registration_email: 'Registration Emails',
      annual_email: 'Annual Emails',
      membership_email: 'Membership Emails',
      refund_request_email: 'Refund Request Emails',
    },
  },
];

// Access Level Card
const AccessLevelCard = styled.div`
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)'};
  border: 1px solid
    ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)'};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const AccessLevelTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
`;

const AccessLevelOptions = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: 500px) {
    flex-direction: column;
  }
`;

const AccessOption = styled.label`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: ${({ selected, theme }) =>
    selected
      ? theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.15)'
        : 'rgba(250, 70, 22, 0.08)'
      : theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : '#f8fafc'};
  border: 2px solid ${({ selected }) => (selected ? '#fa4616' : 'transparent')};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.1)'
        : 'rgba(250, 70, 22, 0.05)'};
  }
`;

const RadioInput = styled.input`
  width: 20px;
  height: 20px;
  accent-color: #fa4616;
  cursor: pointer;
`;

const OptionContent = styled.div``;

const OptionTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin-bottom: 4px;
`;

const OptionDescription = styled.div`
  font-size: 13px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
`;

// Capabilities Grid
const CapabilitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const CapabilityGroup = styled.div`
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)'};
  border: 1px solid
    ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)'};
  border-radius: 12px;
  overflow: hidden;
`;

const GroupHeader = styled.div`
  padding: 16px 20px;
  background: ${({ theme }) =>
    theme.name === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc'};
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)'};
`;

const GroupTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const GroupTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0;
`;

const AllAccessToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
  color: #fa4616;
`;

const GroupDescription = styled.p`
  font-size: 12px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#64748b' : '#94a3b8')};
  margin: 0;
`;

const GroupContent = styled.div`
  padding: 8px 0;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9'};

  &:last-child {
    border-bottom: none;
  }
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#e2e8f0' : '#374151')};
`;

const MiniToggle = styled.label`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  flex-shrink: 0;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const MiniToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;

  &:checked + span {
    background-color: #fa4616;
  }

  &:checked + span:before {
    transform: translateX(18px);
  }
`;

const MiniToggleSlider = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.2)'
      : 'rgba(0, 0, 0, 0.15)'};
  transition: all 0.3s ease;
  border-radius: 11px;

  &:before {
    position: absolute;
    content: '';
    height: 16px;
    width: 16px;
    left: 3px;
    top: 3px;
    background-color: #ffffff;
    transition: all 0.3s ease;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

function Capabilities({
  values,
  handleChange,
  handleMultipleChange,
  mainValue,
  setFieldValue,
  capabilityKeys,
  isSelfEdit,
  isView,
}) {
  const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';

  // Check if all permissions are granted
  let fullAccess = true;
  capabilityKeys.forEach((key) => {
    if (key !== 'account_id') {
      if (!values[key]) fullAccess = false;
    }
  });

  const initialAccess = fullAccess ? 'full' : 'limited';
  const [access, setAccess] = useState(initialAccess);
  const [oldAccess, setOldAccess] = useState();

  const changeAccess = (newAccess) => {
    const newCapability = capabilityKeys.reduce((acc, key) => {
      return key === 'account_id'
        ? { ...acc, account_id: values.account_id }
        : { ...acc, [key]: newAccess };
    }, {});

    const newCapabilities = mainValue.capabilities.reduce((acc, capability) => {
      if (capability.account_id === values.account_id) {
        if (newAccess) {
          setOldAccess(capability);
          return [...acc, newCapability];
        } else {
          return [...acc, oldAccess ? oldAccess : newCapability];
        }
      } else return [...acc, capability];
    }, []);
    setFieldValue('capabilities', newCapabilities);
  };

  const isGroupAllChecked = (groupKeys) => {
    return groupKeys.every((key) => values[key] === true);
  };

  const handleGroupAllAccess = (groupKeys, value) => {
    const changes = groupKeys.reduce((acc, key) => {
      acc[key] = value;
      return acc;
    }, {});
    handleMultipleChange(changes);
  };

  const handleCapabilityChange = (key, value) => {
    handleChange({
      target: {
        name: key,
        value,
      },
    });
  };

  return (
    <Fragment>
      {/* Access Level Selection */}
      {!isSelfEdit && !isView && (
        <AccessLevelCard>
          <AccessLevelTitle>Access Level</AccessLevelTitle>
          <AccessLevelOptions>
            <AccessOption selected={access === 'full'}>
              <RadioInput
                type="radio"
                name="accessLevel"
                value="full"
                checked={access === 'full'}
                onChange={() => {
                  changeAccess(true);
                  setAccess('full');
                }}
              />
              <OptionContent>
                <OptionTitle>Full Access</OptionTitle>
                <OptionDescription>
                  Grant access to all features and permissions
                </OptionDescription>
              </OptionContent>
            </AccessOption>
            <AccessOption selected={access === 'limited'}>
              <RadioInput
                type="radio"
                name="accessLevel"
                value="limited"
                checked={access === 'limited'}
                onChange={() => {
                  changeAccess(false);
                  setAccess('limited');
                }}
              />
              <OptionContent>
                <OptionTitle>Limited Access</OptionTitle>
                <OptionDescription>
                  Choose specific permissions for this user
                </OptionDescription>
              </OptionContent>
            </AccessOption>
          </AccessLevelOptions>
        </AccessLevelCard>
      )}

      {/* Capabilities Grid */}
      {(access === 'limited' || isView) && (
        <CapabilitiesGrid>
          {capabilityGroups
            .filter((group) => !(isTicketHoss && group.hideForTicketHoss))
            .map((group) => (
              <CapabilityGroup key={group.title}>
                <GroupHeader>
                  <GroupTitleRow>
                    <GroupTitle>{group.title}</GroupTitle>
                    {!isSelfEdit && !isView && (
                      <AllAccessToggle>
                        <span>All</span>
                        <MiniToggle>
                          <MiniToggleInput
                            type="checkbox"
                            checked={isGroupAllChecked(group.keys)}
                            onChange={(e) =>
                              handleGroupAllAccess(group.keys, e.target.checked)
                            }
                          />
                          <MiniToggleSlider />
                        </MiniToggle>
                      </AllAccessToggle>
                    )}
                  </GroupTitleRow>
                  <GroupDescription>{group.description}</GroupDescription>
                </GroupHeader>
                <GroupContent>
                  {group.keys.map((key) => (
                    <ToggleRow key={key}>
                      <ToggleLabel>{group.labels[key]}</ToggleLabel>
                      <MiniToggle disabled={isSelfEdit || isView}>
                        <MiniToggleInput
                          type="checkbox"
                          checked={values[key] || false}
                          onChange={(e) =>
                            handleCapabilityChange(key, e.target.checked)
                          }
                          disabled={isSelfEdit || isView}
                        />
                        <MiniToggleSlider />
                      </MiniToggle>
                    </ToggleRow>
                  ))}
                </GroupContent>
              </CapabilityGroup>
            ))}
        </CapabilitiesGrid>
      )}
    </Fragment>
  );
}

export default Capabilities;
